import { GiftPreviewPresetsSection } from './GiftPreviewPresetsSection';
import { AvatarFramesSection } from './AvatarFramesSection';
import { GifteeGiftSection } from './GifteeGiftSection';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { SeasonToggle } from '@src/components/SeasonToggle/SeasonToggle';
import {
  AppHeader,
  Brand,
  Cluster,
  Container,
  PageContent,
  FoldPanel,
  InlineCode,
  Link,
  openFoldPanel,
  Page,
  Panel,
  setFoldPanelsOpen,
  Specimens,
  Stack,
  ThemeToggle,
  Typography,
} from '@src/components';
import { ButtonSection } from '@src/pages/UiKitPage/ButtonSection';
import { CreationHeaderSection } from '@src/pages/UiKitPage/CreationHeaderSection';
import { CardSection } from '@src/pages/UiKitPage/CardSection';
import { CheckboxSection } from '@src/pages/UiKitPage/CheckboxSection';
import { DialogSection } from '@src/pages/UiKitPage/DialogSection';
import { ChoiceSection } from '@src/pages/UiKitPage/ChoiceSection';
import { ExpandableSection } from '@src/pages/UiKitPage/ExpandableSection';
import { HomeSection } from '@src/pages/UiKitPage/HomeSection';
import { InputSection } from '@src/pages/UiKitPage/InputSection';
import { LayoutSection } from '@src/pages/UiKitPage/LayoutSection';
import { NamesInputSection } from '@src/pages/UiKitPage/NamesInputSection';
import { PanelSection } from '@src/pages/UiKitPage/PanelSection';
import { PeopleSection } from '@src/pages/UiKitPage/PeopleSection';
import { RadioSection } from '@src/pages/UiKitPage/RadioSection';
import { SelectSection } from '@src/pages/UiKitPage/SelectSection';
import { StatusSection } from '@src/pages/UiKitPage/StatusSection';
import { SwitchSection } from '@src/pages/UiKitPage/SwitchSection';
import { TagSection } from '@src/pages/UiKitPage/TagSection';
import { ToastSection } from '@src/pages/UiKitPage/ToastSection';
import { TextareaSection } from '@src/pages/UiKitPage/TextareaSection';
import { TokenSection } from '@src/pages/UiKitPage/TokenSection';
import { TypeSection } from '@src/pages/UiKitPage/TypeSection';

import { ThemeArtworkSection } from './ThemeArtworkSection';

const NAV_ITEMS = [
  { href: '#gift-preview-presets', label: 'Gift landscape presets' },
  { href: '#avatar-frames', label: 'Avatar frames' },
  { href: '#giftee-gift', label: 'Giftee gift' },
  { href: '#layout', label: 'Layout' },
  { href: '#creation-header', label: 'Creation' },
  { href: '#type', label: 'Typography' },
  { href: '#buttons', label: 'Buttons' },
  { href: '#toasts', label: 'Toasts' },
  { href: '#dialogs', label: 'Dialogs' },
  { href: '#inputs', label: 'Inputs' },
  { href: '#textareas', label: 'Textareas' },
  { href: '#names', label: 'Names input' },
  { href: '#selects', label: 'Selects' },
  { href: '#checkboxes', label: 'Checkboxes' },
  { href: '#radios', label: 'Radios' },
  { href: '#switches', label: 'Switches' },
  { href: '#tags', label: 'Tags' },
  { href: '#status', label: 'Status' },
  { href: '#people', label: 'People' },
  { href: '#panels', label: 'Panels' },
  { href: '#cards', label: 'Cards' },
  { href: '#home', label: 'Home' },
  { href: '#choices', label: 'Choices' },
  { href: '#expandables', label: 'Expandables' },
  { href: '#season-artwork', label: 'Season artwork' },
  { href: '#theme', label: 'Theme' },
  { href: '#tokens', label: 'Design tokens' },
];

/**
 * The v2 UI kit: every component the design system ships, live and interactive,
 * plus the full token list. Deliberately not translated — it is a tool for
 * whoever is building the UI, not a product screen.
 *
 * Add a component by adding one section. Anything that renders here has to work
 * in both themes, which the toggle in the header makes obvious.
 */
export function UiKitPage() {
  return (
    <Page>
      <AppHeader
        actions={
          <>
            <Link href="/">← Back to the app</Link>
            <SeasonToggle />
            <ThemeToggle />
          </>
        }
      >
        <Cluster gap={6}>
          <Brand />
          <Typography as="span" role="title/eyebrow">
            UI kit
          </Typography>
        </Cluster>
      </AppHeader>

      <PageContent pad="main">
        <Stack gap={8}>
          <Panel>
            <Stack gap={4}>
              <PageHeading title={<>Components</>} />
              <Container width="measure">
                <Typography as="p" role="body/lead">
                  Copy goes through <InlineCode>Typography</InlineCode> and a type role. Spacing and
                  chrome come from the layout blocks in the first section. Placeholder text is the
                  only local type — which is why the theme switch below costs nothing.
                </Typography>
              </Container>
              <Cluster gap={2} as="nav">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    variant="chip"
                    onClick={() => openFoldPanel(item.href.slice(1))}
                  >
                    {item.label}
                  </Link>
                ))}
              </Cluster>
              <Cluster gap={3}>
                <Link onClick={() => setFoldPanelsOpen(true)}>Expand all</Link>
                <Typography as="span" role="meta/helper">
                  ·
                </Typography>
                <Link onClick={() => setFoldPanelsOpen(false)}>Collapse all</Link>
              </Cluster>
            </Stack>
          </Panel>

          <Stack gap={4}>
            <AvatarFramesSection />
          <GiftPreviewPresetsSection />
          <GifteeGiftSection />

            <LayoutSection />

            <CreationHeaderSection />

            <TypeSection />

            <ButtonSection />

            <ToastSection />

            <DialogSection />

            <InputSection />

            <TextareaSection />

            <NamesInputSection />

            <SelectSection />

            <CheckboxSection />

            <RadioSection />

            <SwitchSection />

            <TagSection />

            <StatusSection />

            <PeopleSection />

            <PanelSection />

            <CardSection />

            <HomeSection />

            <ChoiceSection />

            <ExpandableSection />

            <FoldPanel
              id="theme"
              title="Theme toggle"
              hint="Flips data-theme on <html>; the choice is remembered, and defaults to the OS setting."
              example={`<ThemeToggle size="medium" />`}
            >
              <Specimens
                label="Sizes"
                note="Composed entirely from Button — it has no CSS of its own."
              >
                <ThemeToggle size="large" />
                <ThemeToggle size="medium" />
                <ThemeToggle size="small" />
              </Specimens>
            </FoldPanel>

            <ThemeArtworkSection />
            <TokenSection />
          </Stack>
        </Stack>
      </PageContent>
    </Page>
  );
}
